"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useRouter } from "next/navigation";

export type AuthUser = {
  id: number;
  name: string;
  emoji: string;
  owner: "suhyeon" | "wife" | string;
};

type Ctx = {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthCtx = createContext<Ctx>({
  user: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

export function useAuth() {
  return useContext(AuthCtx);
}

/**
 * AuthProvider — SSR이 쿠키 검증으로 풀어준 initialUser 가 있으면 그걸로 바로 시작.
 * 이전 구현은 첫 렌더에서 user=null → useEffect 후 /api/auth/me 응답이 와야 user 채워짐 →
 * 그동안 홈 대시보드가 fallback 카드(수현)를 잠깐 보여주는 깜빡임이 있었다.
 */
export function AuthProvider({
  children,
  initialUser,
}: {
  children: React.ReactNode;
  initialUser?: AuthUser | null;
}) {
  const router = useRouter();
  const [user, setUser] = useState<AuthUser | null>(initialUser ?? null);
  // SSR 이 사용자 줬으면 첫 렌더부터 loading=false
  const [loading, setLoading] = useState<boolean>(!initialUser);

  const refresh = useCallback(async () => {
    try {
      const r = await fetch("/api/auth/me", { cache: "no-store" });
      if (r.ok) {
        const d = await r.json();
        if (d?.ok && d.user) {
          setUser(d.user);
          // 기존 컴포넌트 호환 — useCurrentUser, jarvis profile 동기화
          try {
            localStorage.setItem("budget:currentUser", d.user.owner);
            localStorage.setItem(
              "jarvis.profile",
              JSON.stringify({
                id: d.user.owner,
                name: d.user.name,
                emoji: d.user.emoji,
                createdAt: new Date().toISOString(),
              }),
            );
            queueMicrotask(() => window.dispatchEvent(new Event("jarvis:profile-change")));
          } catch {}
          return;
        }
      }
      setUser(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // SSR 시드가 있으면 굳이 첫 렌더 직후 /me 폴링 안 함 — 토큰 만료 시는 다음 액션에서 401 처리.
    // 시드가 없을 때만 클라이언트에서 /me 재시도 (예: SSR 실패/캐시 회피).
    if (!initialUser) {
      refresh();
    } else {
      // localStorage 동기화는 즉시
      try {
        localStorage.setItem("budget:currentUser", initialUser.owner);
        localStorage.setItem(
          "jarvis.profile",
          JSON.stringify({
            id: initialUser.owner,
            name: initialUser.name,
            emoji: initialUser.emoji,
            createdAt: new Date().toISOString(),
          }),
        );
        queueMicrotask(() => window.dispatchEvent(new Event("jarvis:profile-change")));
      } catch {}
    }
  }, [refresh, initialUser]);

  const logout = useCallback(async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {}
    try {
      localStorage.removeItem("budget:currentUser");
      localStorage.removeItem("jarvis.profile");
      queueMicrotask(() => window.dispatchEvent(new Event("jarvis:profile-change")));
    } catch {}
    setUser(null);
    router.replace("/login");
    router.refresh();
  }, [router]);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
